import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import {
  FileText,
  Plus,
  Edit3,
  Trash2,
  Copy,
  Star,
  StarOff,
  Tag,
  Clock,
  Search,
} from 'lucide-react';

export interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  body?: string;
  bodyHtml?: string;
  bodyText?: string;
  category: 'sales' | 'support' | 'follow-up' | 'general' | 'custom';
  tags: string[];
  isFavorite: boolean;
  usageCount: number;
  lastUsed?: string;
  createdAt: string;
  updatedAt: string;
}

interface EmailTemplatesProps {
  onSelectTemplate: (template: EmailTemplate) => void;
  onClose?: () => void;
}

// Default templates (will be seeded to backend on first load)
const defaultTemplates: Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: 'Welcome Email',
    subject: 'Welcome to DomElec Solar Solutions',
    body: `Dear [Customer Name],

Thank you for your interest in DomElec Solar Solutions! We're excited to help you transition to clean, renewable energy.

Our team will review your requirements and get back to you within 24 hours with a customized solar solution that fits your needs and budget.

In the meantime, feel free to explore our website or contact us if you have any questions.

Best regards,
The DomElec Team`,
    category: 'sales',
    tags: ['welcome', 'new-customer'],
    isFavorite: true,
    usageCount: 45,
  },
  {
    name: 'Quote Follow-up',
    subject: 'Your Solar Quote from DomElec',
    body: `Hi [Customer Name],

I wanted to follow up on the solar quote we sent you last week. Have you had a chance to review it?

I'm happy to answer any questions you might have or discuss how we can adjust the proposal to better meet your needs.

Would you be available for a quick call this week to discuss?

Looking forward to hearing from you!

Best regards,
[Your Name]`,
    category: 'follow-up',
    tags: ['quote', 'follow-up', 'sales'],
    isFavorite: true,
    usageCount: 32,
  },
  {
    name: 'Installation Scheduled',
    subject: 'Your Solar Installation is Scheduled',
    body: `Dear [Customer Name],

Great news! Your solar installation has been scheduled for [Date] at [Time].

Our installation team will arrive at your property and the installation typically takes [Duration]. Please ensure someone is available at the property during this time.

What to expect:
- Our team will review the installation plan with you
- Installation of solar panels and inverter
- System testing and commissioning
- Final walkthrough and documentation

If you need to reschedule or have any questions, please don't hesitate to contact us.

Best regards,
The DomElec Installation Team`,
    category: 'general',
    tags: ['installation', 'scheduling'],
    isFavorite: false,
    usageCount: 28,
  },
  {
    name: 'Support Response',
    subject: 'Re: Your Support Request',
    body: `Hi [Customer Name],

Thank you for contacting DomElec support.

[Response to issue]

If you need any further assistance, please don't hesitate to reach out.

Best regards,
[Your Name]
DomElec Support Team`,
    category: 'support',
    tags: ['support', 'response'],
    isFavorite: false,
    usageCount: 18,
  },
];

export function EmailTemplates({ onSelectTemplate, onClose }: EmailTemplatesProps) {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [newTemplate, setNewTemplate] = useState<Partial<EmailTemplate>>({
    name: '',
    subject: '',
    body: '',
    category: 'general',
    tags: [],
  });

  // Fetch templates from backend
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['/api/emails/templates'],
    queryFn: () => apiRequest<EmailTemplate[]>('GET', '/api/emails/templates'),
  });

  // Create template mutation
  const createTemplateMutation = useMutation({
    mutationFn: (template: Partial<EmailTemplate>) =>
      apiRequest<EmailTemplate>('POST', '/api/emails/templates', template),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/emails/templates'] });
      setShowCreateDialog(false);
      setNewTemplate({
        name: '',
        subject: '',
        body: '',
        category: 'general',
        tags: [],
      });
      toast({
        title: 'Template Created',
        description: 'Your template has been saved successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to create template: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Update template mutation
  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, ...data }: EmailTemplate) =>
      apiRequest<EmailTemplate>('PUT', `/api/emails/templates/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/emails/templates'] });
      toast({
        title: 'Template Updated',
        description: 'Your template has been updated successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to update template: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: (id: number) =>
      apiRequest('DELETE', `/api/emails/templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/emails/templates'] });
      toast({
        title: 'Template Deleted',
        description: 'The template has been removed.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to delete template: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Track template usage mutation
  const trackUsageMutation = useMutation({
    mutationFn: (id: number) =>
      apiRequest<EmailTemplate>('POST', `/api/emails/templates/${id}/usage`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/emails/templates'] });
    },
  });

  // Filter templates
  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const handleUseTemplate = (template: EmailTemplate) => {
    // Track usage
    trackUsageMutation.mutate(template.id);

    // Use the template
    onSelectTemplate({
      ...template,
      body: template.bodyText || template.body || ''
    });

    toast({
      title: 'Template Applied',
      description: `"${template.name}" template has been applied to your email.`,
    });
  };

  const handleToggleFavorite = (template: EmailTemplate) => {
    updateTemplateMutation.mutate({
      ...template,
      isFavorite: !template.isFavorite,
    });
  };

  const handleCreateTemplate = () => {
    if (!newTemplate.name || !newTemplate.subject || !newTemplate.body) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return;
    }

    createTemplateMutation.mutate({
      ...newTemplate,
      bodyText: newTemplate.body,
      bodyHtml: newTemplate.body,
    });
  };

  const handleDeleteTemplate = (templateId: number) => {
    deleteTemplateMutation.mutate(templateId);
  };

  const categories = [
    { value: 'all', label: 'All Templates' },
    { value: 'sales', label: 'Sales' },
    { value: 'support', label: 'Support' },
    { value: 'follow-up', label: 'Follow-up' },
    { value: 'general', label: 'General' },
    { value: 'custom', label: 'Custom' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Email Templates</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCreateDialog(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Create Template
          </Button>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.value} value={category.value}>
                  {category.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Templates List */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-3">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50 animate-pulse" />
              <p>Loading templates...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No templates found</p>
            </div>
          ) : (
            filteredTemplates.map((template) => (
              <Card
                key={template.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold">{template.name}</h4>
                        <Badge variant="outline" className="text-xs">
                          {template.category}
                        </Badge>
                        {template.isFavorite && (
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        )}
                      </div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">
                        {template.subject}
                      </p>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {template.body}
                      </p>
                    </div>
                  </div>

                  {/* Tags and Stats */}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex flex-wrap gap-1">
                      {template.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          <Tag className="h-3 w-3 mr-1" />
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Used {template.usageCount} times
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      size="sm"
                      onClick={() => handleUseTemplate(template)}
                      className="flex-1"
                    >
                      Use Template
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleFavorite(template)}
                      className="p-2"
                    >
                      {template.isFavorite ? (
                        <StarOff className="h-4 w-4" />
                      ) : (
                        <Star className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingTemplate(template)}
                      className="p-2"
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    {template.category === 'custom' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="p-2 text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Create Template Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Email Template</DialogTitle>
            <DialogDescription>
              Create a reusable email template for common messages.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Template Name</Label>
              <Input
                placeholder="e.g., Welcome Email"
                value={newTemplate.name}
                onChange={(e) =>
                  setNewTemplate((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>

            <div>
              <Label>Category</Label>
              <Select
                value={newTemplate.category}
                onValueChange={(value) =>
                  setNewTemplate((prev) => ({ ...prev, category: value as EmailTemplate['category'] }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="follow-up">Follow-up</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Subject</Label>
              <Input
                placeholder="Email subject line"
                value={newTemplate.subject}
                onChange={(e) =>
                  setNewTemplate((prev) => ({ ...prev, subject: e.target.value }))
                }
              />
            </div>

            <div>
              <Label>Message Body</Label>
              <Textarea
                placeholder="Type your template message... Use [Customer Name] for placeholders."
                value={newTemplate.body}
                onChange={(e) =>
                  setNewTemplate((prev) => ({ ...prev, body: e.target.value }))
                }
                className="min-h-[200px]"
              />
            </div>

            <div>
              <Label>Tags (comma-separated)</Label>
              <Input
                placeholder="e.g., welcome, onboarding, sales"
                onChange={(e) =>
                  setNewTemplate((prev) => ({
                    ...prev,
                    tags: e.target.value.split(',').map((t) => t.trim()),
                  }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTemplate}>Create Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}